import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  TextField,
} from "@mui/material";
import PerfectScrollbar from "react-perfect-scrollbar";

import "../../vendor/perfect-scrollbar.css";

export interface FilterListProps {
  loading?: boolean;
  disabled?: boolean;
  canSearch?: boolean;
  canSave?: boolean;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  onReset?: (event: React.FormEvent<HTMLFormElement>) => void;
  onSave?: (title: string) => void;
  children?: React.ReactNode;
}

const FilterList: React.FC<FilterListProps> = ({ loading, disabled, canSearch, canSave, children, onSubmit, onReset, onSave }) => {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");

  const getDefaultTitle = useCallback(() => {
    const now = new Date();
    const date = now.toLocaleDateString();
    const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return `Search ${date} ${time}`;
  }, []);

  const handleSaveClick = () => {
    setSaveTitle(getDefaultTitle());
    setSaveDialogOpen(true);
  };

  const handleSaveDialogClose = () => {
    setSaveDialogOpen(false);
  };

  return (
    <Box component="form" height="100%" display="flex" flexDirection="column" onSubmit={onSubmit} onReset={onReset}>
      <PerfectScrollbar>{children}</PerfectScrollbar>
      <Divider />
      <Box padding={4}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              fullWidth
              disabled={loading || disabled || !canSearch}
              type="submit"
              data-testid="runSearch"
            >
              Run search
            </Button>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              type="button"
              disabled={loading || disabled || !canSave}
              onClick={handleSaveClick}
            >
              Save
            </Button>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Button variant="outlined" size="large" fullWidth type="reset" disabled={disabled}>
              Reset
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Dialog
        open={saveDialogOpen}
        onClose={handleSaveDialogClose}
        PaperProps={{
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const trimmedTitle = saveTitle.trim() || getDefaultTitle();
            onSave?.(trimmedTitle);
            handleSaveDialogClose();
          },
        }}
      >
        <Box marginY={8}>
          <DialogTitle>Save search</DialogTitle>
          <DialogContent>
            <DialogContentText>Please enter a name for your search:</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="filter-list-search-title"
              name="title"
              label=""
              type="text"
              value={saveTitle}
              onChange={event => setSaveTitle(event.target.value)}
              fullWidth
              variant="outlined"
            />
          </DialogContent>
          <Box marginRight={8}>
            <DialogActions>
              <Button variant="outlined" type="button" onClick={handleSaveDialogClose}>
                Cancel
              </Button>
              <Button variant="contained" type="submit">
                Save
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default FilterList;
